import type { ConfigProps } from '../../configTypes';
import { getElementAttributes, componentsToEnrich } from '../utils';
import fetchRankingsData from '../queries/fetchRankingsData';
import transformHero from '../transform/transformHero';
import fetchEmbeddedPosts from '../queries/fetchEmbeddedPosts';
import fetchSingleEmbeddedPost from '../queries/fetchSingleEmbeddedPost';
import fetchEmbeddedPostsByCat from '../queries/fetchEmbeddedPostsByCat';
import fetchEmbeddedContributors from '../queries/fetchEmbeddedContributors';
import fetchGravityForms from '../queries/fetchGravityForms';
import getByline from '../lib/getByline';
import transformSeoContentData from '../transform/transformSeoContentData';
import getSeoSchemasToRender from '../seo/getSeoSchemasToRender';
import { getRankingIdsFromShortcodes } from '../utils/getRankingIdsFromShortcodes';
import { getSeoFromShortcodes } from '../utils/getSeoFromShortcodes';
import type {
  EnrichedContent as EnrichedContentType,
  TocItem,
  TransformedItemPost,
  TransformedItemContributor,
} from '../types';

export default async function enrichContent(
  post: {
    content?: string;
    contributors?: unknown;
    customDateType?: string;
    date?: string;
    additionalBylineDescription?: string;
    featuredImage?: unknown;
    seo?: { canonicalUrl?: string; openGraph?: { description?: string } };
    title?: string;
  } | null,
  enrichedSiteSettings: ConfigProps,
): Promise<EnrichedContentType> {
  const enrichedContent: EnrichedContentType = {};
  if (!post?.content) return enrichedContent;

  const componentData: Record<string, Record<string, unknown>[]> = {};
  for (const component of componentsToEnrich) {
    componentData[component] = getElementAttributes(component, post.content!);
  }

  if (componentData['sonic-gravityform']?.length) {
    const formIds = componentData['sonic-gravityform']
      .map((d) => Number((d as { id?: string }).id))
      .filter(Boolean);
    if (formIds.length) {
      enrichedContent.gravityForms = await fetchGravityForms(
        enrichedSiteSettings.cmsUrl,
        formIds,
      );
    }
  }

  const rankingIds = getRankingIdsFromShortcodes(post.content ?? '');

  if (rankingIds.length > 0) {
    const rankings: Record<string, unknown> = {};
    const { domain, cmsUrl } = enrichedSiteSettings;
    await Promise.all(
      rankingIds.map(async (id) => {
        try {
          const data = await fetchRankingsData(id, domain, cmsUrl);
          if (data) rankings[id] = data;
        } catch (e) {
          console.error('Error processing ranking:', e);
        }
      }),
    );
    if (Object.keys(rankings).length > 0) enrichedContent.rankings = rankings;
  }

  if (
    componentData['sonic-contributor-byline']?.length ||
    componentData['sonic-contributor-highlight']?.length ||
    componentData['sonic-hero']?.length
  ) {
    enrichedContent.byline = post.content
      ? await getByline(post, enrichedSiteSettings)
      : null;
    if (componentData['sonic-hero']?.length) {
      enrichedContent['sonic-hero'] = await transformHero(
        componentData['sonic-hero'][0],
        post as {
          title?: string;
          content?: string;
          seo?: { canonicalUrl?: string; openGraph?: { description?: string } };
          featuredImage?: unknown;
        },
        enrichedSiteSettings,
        enrichedContent.byline,
      );
    }
  }

  let embeddedPosts: Record<string, TransformedItemPost> = {};
  const embeddedPostsByCategories: Record<string, TransformedItemPost[]> = {};

  const postComponents = [
    ...(componentData['sonic-posts'] || []),
    ...(componentData['sonic-card'] || []),
    ...(componentData['sonic-bc-cards'] || []),
    ...(componentData['sonic-field-of-study'] || []),
    ...(componentData['sonic-wayfinder'] || []),
    ...(componentData['sonic-animated-articles'] || []),
  ].filter(
    (p) =>
      (p as Record<string, string>)['posts_in'] ||
      (p as Record<string, string>)['post_id'] ||
      (p as Record<string, string>)['posts_in_associate_degrees'] ||
      (p as Record<string, string>)['posts_in_bachelors_degrees'] ||
      (p as Record<string, string>)['posts_in_masters_degrees'] ||
      (p as Record<string, string>)['posts_in_doctoral_degrees'] ||
      (p as Record<string, string>)['posts_in_associate_rankings'] ||
      (p as Record<string, string>)['posts_in_bachelors_rankings'] ||
      (p as Record<string, string>)['posts_in_masters_rankings'] ||
      (p as Record<string, string>)['posts_in_doctoral_rankings'],
  );

  if (postComponents.length) {
    const embeddedPostsIds = postComponents.map((p) => {
      const fields = [
        (p as Record<string, string>)['posts_in'],
        (p as Record<string, string>)['post_id'],
        (p as Record<string, string>)['posts_in_associate_degrees'],
        (p as Record<string, string>)['posts_in_bachelors_degrees'],
        (p as Record<string, string>)['posts_in_masters_degrees'],
        (p as Record<string, string>)['posts_in_doctoral_degrees'],
        (p as Record<string, string>)['posts_in_associate_rankings'],
        (p as Record<string, string>)['posts_in_bachelors_rankings'],
        (p as Record<string, string>)['posts_in_masters_rankings'],
        (p as Record<string, string>)['posts_in_doctoral_rankings'],
      ];
      return fields.filter(Boolean).join(',');
    });

    const allIds = embeddedPostsIds.flatMap((s) =>
      s.split(',').map((id) => id.trim()),
    );
    const uniqueIds = allIds.filter((id, i) => allIds.indexOf(id) === i);
    const [numericIds, nonNumericIds] = uniqueIds.reduce<[string[], string[]]>(
      (acc, id) => {
        if (/^\d+$/.test(id)) acc[0].push(id);
        else acc[1].push(id);
        return acc;
      },
      [[], []],
    );

    if (numericIds.length) {
      const fetched = await fetchEmbeddedPosts(
        numericIds,
        enrichedSiteSettings,
      );
      if (fetched) embeddedPosts = { ...fetched };
    }
    await Promise.all(
      nonNumericIds.map(async (id) => {
        const data = await fetchSingleEmbeddedPost(
          id,
          enrichedSiteSettings.postTypeGql,
          enrichedSiteSettings.cmsUrl,
          enrichedSiteSettings.fallbackPostImage,
        );
        if (data?.[id]) embeddedPosts[id] = data[id];
      }),
    );

    const embeddedCategories = [
      ...(componentData['sonic-posts'] || []),
      ...(componentData['sonic-bc-cards'] || []),
    ].filter((p) => (p as Record<string, string>)['category']);

    for (const item of embeddedCategories) {
      const category = (item as Record<string, string>)['category'];
      const limit = (item as Record<string, string>)['limit'];
      const orderBy = (item as Record<string, string>)['order'] as
        | 'updated'
        | 'published'
        | 'custom'
        | undefined;
      try {
        const data = await fetchEmbeddedPostsByCat(
          category,
          { limit, orderBy },
          enrichedSiteSettings.postTypeGql,
          enrichedSiteSettings.cmsUrl,
          enrichedSiteSettings.fallbackPostImage,
        );
        if (data) embeddedPostsByCategories[category] = data;
      } catch (e) {
        console.error('Error fetching embedded posts by category:', e);
      }
    }
  }

  let embeddedContributors: Record<string, TransformedItemContributor> = {};
  const contributorComponents = [
    ...(componentData['contributor-spotlight'] || []),
    ...(componentData['sonic-list'] || []),
  ]
    .filter((c) => (c as Record<string, string>)['contributorid'])
    .map((c) => (c as Record<string, string>)['contributorid']);
  const contributorIds = contributorComponents.flatMap((s) =>
    s.split(',').map((id) => id.trim()),
  );
  const uniqueContributorIds = contributorIds.filter(
    (id, i) => contributorIds.indexOf(id) === i,
  );
  const numericContributorIds = uniqueContributorIds.filter((id) =>
    /^\d+$/.test(id),
  );
  if (numericContributorIds.length) {
    const fetched = await fetchEmbeddedContributors(
      numericContributorIds,
      enrichedSiteSettings,
    );
    if (fetched) embeddedContributors = { ...fetched };
  }

  enrichedContent.embeddedPosts =
    Object.keys(embeddedPosts).length > 0 ? embeddedPosts : null;
  enrichedContent.embeddedPostsByCategories =
    Object.keys(embeddedPostsByCategories).length > 0
      ? embeddedPostsByCategories
      : null;
  const anchorItems = componentData['a'] || [];
  enrichedContent.tocItems = anchorItems
    .filter((item) => (item as unknown as TocItem).class === 'subnav-item')
    .map((item) => {
      const t = item as unknown as TocItem;
      return { title: t.name, href: '#' + t.id };
    });
  enrichedContent.embeddedContributors =
    Object.keys(embeddedContributors).length > 0 ? embeddedContributors : null;

  const seoContentData = getSeoFromShortcodes(post.content ?? '');

  enrichedContent.seoTransformedContentData =
    transformSeoContentData(seoContentData);

  enrichedContent.seoSchemasToRender = getSeoSchemasToRender(enrichedContent);

  return enrichedContent;
}
